import { guid } from "@dextall/common-utilities";
import { getLouvreOverlapsSetting, type SystemSettings } from "../../../responses/systemSettings";
import type {
    CladdingCellStyle,
    ILouvreCurtainCircularCutout,
    ILouvreCurtainRectangularCutout,
    IPanelSourceLouvre,
} from "../../../responses/panelSource";
import type { CircularCutout, Louvre, RectangularCutout } from "@dextall/louvre-curtain-cutout-2d-editor";
import type { WindowSettings, PanelLouvre } from "@dextall/panel-hooks-editor-2d";

export const createLouvreSourceDefinition = (
    panelOrigin: THREE.Vector3,
    panelLouvreDefinition: IPanelSourceLouvre,
    systemSetting: SystemSettings,
): PanelLouvre => {
    const frameOffset = systemSetting.louvreFrameOffset;

    return {
        box: {
            min: {
                x: convertToMetric(panelLouvreDefinition.box.min.x - panelOrigin.x + frameOffset.left),
                y: convertToMetric(panelLouvreDefinition.box.min.y - panelOrigin.y + frameOffset.bottom),
            },
            max: {
                x: convertToMetric(panelLouvreDefinition.box.max.x - panelOrigin.x - frameOffset.right),
                y: convertToMetric(panelLouvreDefinition.box.max.y - panelOrigin.y - frameOffset.top),
            },
        },
        sillColor: panelLouvreDefinition.sillColor,
    };
};

export const createEditor2DLouvreSystemSettings = (
    systemSetting: SystemSettings,
    panelStyle: CladdingCellStyle,
): WindowSettings => {
    const { louvreFrameOffset } = systemSetting;
    const louvreOverlaps = getLouvreOverlapsSetting(systemSetting, panelStyle);
    return {
        overlappingSettings: {
            left: convertToMetric(louvreOverlaps.left),
            right: convertToMetric(louvreOverlaps.right),
            top: convertToMetric(louvreOverlaps.top),
            bottom: convertToMetric(louvreOverlaps.bottom),
        },
        frameOffset: {
            left: convertToMetric(louvreFrameOffset.left),
            right: convertToMetric(louvreFrameOffset.right),
            top: convertToMetric(louvreFrameOffset.top),
            bottom: convertToMetric(louvreFrameOffset.bottom),
        },
    };
};

export const createEditor2DLouvre = (openingWidth: number, openingHeight: number): Louvre => {
    return {
        width: convertToMetric(openingWidth),
        height: convertToMetric(openingHeight),
    };
};

export const toEditorLouvreCurtainRectangularCutouts = (
    rectangularCutouts: ILouvreCurtainRectangularCutout[],
): RectangularCutout[] => {
    return rectangularCutouts.map(cutout => ({
        id: guid(),
        box: {
            min: {
                x: convertToMetric(cutout.box.min.x),
                y: convertToMetric(cutout.box.min.y),
            },
            max: {
                x: convertToMetric(cutout.box.max.x),
                y: convertToMetric(cutout.box.max.y),
            },
        },
    }));
};

export const toEditorLouvreCurtainCircularCutouts = (
    circularCutouts: ILouvreCurtainCircularCutout[],
): CircularCutout[] => {
    return circularCutouts.map(cutout => ({
        id: guid(),
        center: {
            x: convertToMetric(cutout.center.x),
            y: convertToMetric(cutout.center.y),
        },
        radius: convertToMetric(cutout.radius),
    }));
};

export const toDomainLouvreCurtainRectangularCutouts = (
    rectangularCutouts: RectangularCutout[],
): ILouvreCurtainRectangularCutout[] => {
    return rectangularCutouts.map(cutout => ({
        box: new THREE.Box3(
            new THREE.Vector3(convertToImperial(cutout.box.min.x), convertToImperial(cutout.box.min.y), 0),
            new THREE.Vector3(convertToImperial(cutout.box.max.x), convertToImperial(cutout.box.max.y), 0),
        ),
    }));
};

export const toDomainLouvreCurtainCircularCutouts = (
    circularCutouts: CircularCutout[],
): ILouvreCurtainCircularCutout[] => {
    return circularCutouts.map(cutout => ({
        center: new THREE.Vector3(convertToImperial(cutout.center.x), convertToImperial(cutout.center.y), 0),
        radius: convertToImperial(cutout.radius),
    }));
};

const convertToMetric = (value: number) => Autodesk.Viewing.Private.convertUnits("ft", "mm", 1, value);
const convertToImperial = (value: number) => Autodesk.Viewing.Private.convertUnits("mm", "ft", 1, value);
